import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import {
  ChevronUp,
  LayoutDashboard,
  LogIn,
  MessageCircle,
  Search,
} from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { useChatsQuery } from "../api/chatApi.ts";
import StudioSheet from "./StudioSheet.tsx";

export default function AppBottomNav() {
  const auth = useAuth();
  const location = useLocation();
  const [studioSheetOpen, setStudioSheetOpen] = useState(false);

  const { data: chats } = useChatsQuery({ refetchInterval: 60000 });

  const unreadChats = useMemo(() => {
    if (!chats || chats.length === 0) return 0;
    return chats.reduce((total, chat) => total + (chat.unreadCount || 0), 0);
  }, [chats]);

  const isExplore =
    location.pathname === "/" || location.pathname.startsWith("/search");
  const isInStudio =
    location.pathname.startsWith("/mystudio") &&
    !location.pathname.startsWith("/mystudio/messages");

  function login() {
    auth
      .signinRedirect({ redirect_uri: window.location.href })
      .catch(console.error);
  }

  const tabCls = (active: boolean) =>
    `flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] leading-none transition-colors ${
      active ? "text-soluud-primary" : "text-soluud-text-tertiary"
    }`;

  const iconCls = (active: boolean) =>
    `h-5.5 w-5.5 shrink-0 ${
      active ? "text-soluud-primary" : "text-soluud-text-tertiary"
    }`;

  return (
    <>
      <StudioSheet
        isOpen={studioSheetOpen}
        onClose={() => setStudioSheetOpen(false)}
      />

      <nav
        className="border-soluud-border fixed right-0 bottom-0 left-0 z-40 border-t bg-white/85 backdrop-blur-lg sm:hidden"
        aria-label="Bottom navigation"
      >
        <ul className="flex">
          {/* Explore */}
          <li className="flex flex-1">
            <NavLink
              to="/search"
              className={tabCls(isExplore)}
              aria-label="Explore"
              onClick={() => setStudioSheetOpen(false)}
            >
              <Search className={iconCls(isExplore)} />
              <span className="truncate px-1">
                <Trans>Explore</Trans>
              </span>
            </NavLink>
          </li>

          {auth.isAuthenticated ? (
            <>
              {/* Messages */}
              <li className="flex flex-1">
                <NavLink
                  to="/mystudio/messages"
                  className={({ isActive }) => tabCls(isActive)}
                  aria-label="Messages"
                  onClick={() => setStudioSheetOpen(false)}
                >
                  <div className="relative">
                    <MessageCircle
                      className={iconCls(
                        location.pathname.startsWith("/mystudio/messages"),
                      )}
                    />
                    {unreadChats > 0 && (
                      <span className="bg-soluud-warning absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white shadow-sm">
                        {unreadChats > 9 ? "9+" : unreadChats}
                      </span>
                    )}
                  </div>
                  <span className="truncate px-1">
                    <Trans>Messages</Trans>
                  </span>
                </NavLink>
              </li>

              {/* My Studio */}
              <li className="flex flex-1">
                <button
                  onClick={() => setStudioSheetOpen((open) => !open)}
                  className={tabCls(isInStudio || studioSheetOpen)}
                  aria-label="My Studio"
                  aria-haspopup="dialog"
                  aria-expanded={studioSheetOpen}
                >
                  <LayoutDashboard
                    className={iconCls(isInStudio || studioSheetOpen)}
                  />
                  <span className="flex items-center gap-0.5 truncate px-1">
                    <Trans>My Studio</Trans>
                    <ChevronUp
                      className={`h-3 w-3 shrink-0 transition-transform ${studioSheetOpen ? "rotate-180" : ""}`}
                    />
                  </span>
                </button>
              </li>
            </>
          ) : (
            /* Sign in */
            <li className="flex flex-1">
              <button
                onClick={login}
                className={tabCls(false)}
                aria-label="Sign in"
              >
                <LogIn className={iconCls(false)} />
                <span className="truncate px-1">
                  <Trans>Sign in</Trans>
                </span>
              </button>
            </li>
          )}
        </ul>
      </nav>
    </>
  );
}
